import React, { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { validateEmail } from "../utils/passwordValidation";
import { useAuth } from "../context/AuthContext";

const LoginUser = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const { login } = useAuth();

  const navigate = useNavigate();

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    const error = validateEmail(newEmail);
    setEmailError(error);
  };

  // Handle password change with basic validation
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    // For login, we just check if password is empty
    const error = newPassword.trim() ? "" : "Password is required";
    setPasswordError(error);
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Validate fields
    const emailValidationError = validateEmail(email);
    const passwordValidationError = password.trim()
      ? ""
      : "Password is required";

    setEmailError(emailValidationError);
    setPasswordError(passwordValidationError);

    // If validation fails, don't submit
    if (emailValidationError || passwordValidationError) {
      return;
    }

    try {
      const response = await login(email, password, isAdmin);

      // Show success message
      toast.success("Login successful!");

      if (response.isAdmin) {
        if (response.adminRole === "SuperAdmin") {
          navigate("/DsportSuperAdmin", { replace: true });
        } else if (response.adminRole === "Admin") {
          navigate("/DsportAdmin", { replace: true });
        }
        return;
      }

      if (response.isTester) {
        const accessRights = response.testerAccessRights || {};
        if (accessRights.all) {
          navigate("/main-dashboard", { replace: true });
        } else if (accessRights.arena) {
          navigate("/user-dashboard", { replace: true });
        } else if (accessRights.news) {
          navigate("/main-dashboard", { replace: true });
        } else if (accessRights["player_profiling"]) {
          navigate("/Manage", { replace: true });
        } else if (accessRights.informatica) {
          navigate("/search", { replace: true });
        } else {
          navigate("/main-dashboard", { replace: true });
        }
        return;
      }

      // regular user
      navigate("/main-dashboard", { replace: true });
    } catch (err) {
      // Show error message using toast
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Login failed. Please try again."
      );
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-[#f0f2f5] px-4 py-6">
      <div className="bg-white p-6 sm:p-10 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.05)] w-full max-w-[400px] backdrop-blur-[10px] border border-white/20">
        <img
          src="/logo.png"
          alt="Sports Hub Logo"
          className="block mx-auto mb-4 sm:mb-6 h-[50px] sm:h-[60px] drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
        />
        <h2 className="text-center text-[#2f3542] mb-4 sm:mb-6 font-bold text-xl sm:text-2xl tracking-[-0.5px]">
          User Login
        </h2>
        <form onSubmit={handleLogin} className="flex flex-col">
          <div className="mb-5">
            <label htmlFor="email" className="block mb-1.5 text-[#57606f]">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email"
              required
              className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 ${
                emailError
                  ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                  : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              } focus:outline-none`}
            />
            {emailError && (
              <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                <span className="text-xs">⚠</span>
                {emailError}
              </div>
            )}
          </div>
          <div className="mb-5">
            <label htmlFor="password" className="block mb-1.5 text-[#57606f]">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={handlePasswordChange}
                placeholder="Enter your password"
                required
                className={`w-full h-11 px-3 pr-12 rounded-lg text-base border transition-all duration-200 ${
                  passwordError
                    ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                    : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                } focus:outline-none`}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#f8f9fa] border border-[#dee2e6] rounded-md cursor-pointer text-[#6c757d] text-sm p-1.5 flex items-center justify-center min-w-[32px] h-8 transition-all duration-200 hover:bg-[#e9ecef] hover:text-[#495057] hover:border-[#adb5bd] hover:scale-105 active:scale-95 focus:outline-2 focus:outline-[#007bff] focus:outline-offset-2"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {passwordError && (
              <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                <span className="text-xs">⚠</span>
                {passwordError}
              </div>
            )}
          </div>
          <button
            type="submit"
            className="w-full h-11 bg-[#3498db] text-white border-none rounded-lg cursor-pointer transition-all duration-300 text-base font-semibold uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
          >
            Login
          </button>
          <button
            type="button"
            onClick={() => navigate("/reg-verification")}
            className="w-full h-11 mt-3 bg-white text-[#3498db] border border-[#3498db] rounded-lg cursor-pointer transition-all duration-300 text-base font-semibold hover:bg-[#f0f8ff] hover:scale-105 active:scale-100"
          >
            Register
          </button>
          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            className="mt-4 text-sm text-[#3498db] bg-transparent border-none cursor-pointer text-center w-full hover:underline"
          >
            Forgot Password?
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginUser;
